import { EventType } from '@graphql/generated-base';
import type { RsvpRoute } from './rsvpRoute.types';

export type { RsvpRoute } from './rsvpRoute.types';

/**
 * Resolves the `/edit/<path>` RSVP routes for an event.
 *
 * The legacy `rsvp` admin reads the legacy RSVP API by `firebaseId`, so it fails for any event with
 * no Firebase document behind it: the ACL lookup 404s and the screens render empty or error. Same
 * root cause as the legacy `guests` route — see `resolveGuestListRoute`.
 *
 * A new-stack wedding, a general event and a card event use the registration screen instead, which
 * is what the workspace nav already sends them to (`wedding_new_stack.ts`, `general_default.ts`, and
 * `useEventSubTypeConfig` maps `EventType.card` to the general config). Tracking
 * maps onto that screen's own `tracking` tab rather than a separate route; `tab` is a managed search
 * param on the registration admin (see `RegistrationContext`).
 *
 * An unresolved `eventType` defaults to the legacy routes, so an old-stack wedding keeps today's
 * behavior while its event info is still loading — matching `resolveGuestListRoute`.
 *
 * Both destinations are joy-web SPA routes (registered in `Admin.controller.tsx`), so callers keep
 * whatever `pathIsInJoyWeb` they already declared.
 */
export const resolveRsvpRoute = (
  args: Readonly<{
    eventType: EventType | null | undefined;
    isNewStackEvent: boolean;
  }>,
): RsvpRoute => {
  const { eventType, isNewStackEvent } = args;

  const needsFirebaseFreeRsvp = isNewStackEvent || eventType === EventType.general || eventType === EventType.card;
  if (!needsFirebaseFreeRsvp) {
    return { manage: 'rsvp', tracking: 'rsvp/answers', supportsActivityTab: true };
  }

  return { manage: 'registration', tracking: 'registration?tab=tracking', supportsActivityTab: false };
};
